/**
 * Shared phone number validation utilities.
 *
 * Rules:
 *  - Exactly 10 digits after stripping spaces, dashes, and parentheses.
 *  - Must start with 6, 7, 8, or 9 (Indian mobile numbers).
 *  - Fields are always optional — empty / blank is valid.
 *
 * Usage (Zod schema):
 *   phone: phoneZodSchema
 *
 * Usage (manual):
 *   const err = validatePhone(rawValue);   // returns error string or null
 *   const cleaned = stripPhone(rawValue);  // digits only
 */

import { z } from "zod";

/** Strips non-digits and normalizes +91 / 0 prefixes to 10 digits. */
export function stripPhone(value: string): string {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits;
}

/**
 * Returns an error message if the phone number is invalid, null if valid (or empty).
 */
export function validatePhone(value: string | null | undefined): string | null {
  if (!value || value.trim() === "") return null; // optional — blank is OK
  const digits = stripPhone(value);
  if (digits.length !== 10) return "Phone number must be exactly 10 digits";
  if (!/^[6-9]/.test(digits)) return "Phone number must start with 6, 7, 8, or 9";
  return null;
}

/**
 * Zod schema for an optional Indian phone number field.
 * Accepts empty string or a valid 10-digit mobile number.
 */
export const phoneZodSchema = z
  .string()
  .trim()
  .refine(
    (v) => {
      if (!v) return true; // empty is OK
      const digits = stripPhone(v);
      return digits.length === 10 && /^[6-9]/.test(digits);
    },
    { message: "Enter a valid 10-digit mobile number (starting with 6-9)" }
  )
  .optional()
  .or(z.literal(""));
